import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { notificationsAPI, messagesAPI, uploadAPI, postsAPI, storiesAPI, musicAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { Toaster, toast } from 'sonner';
import GlobalCallOverlay from '../../components/layout/GlobalCallOverlay';
import {
  House, Compass, Clapperboard, MessageSquare, BellRing, SquarePlus,
  CircleUser, MoreHorizontal, Bookmark, SlidersHorizontal, ShieldCheck, LogOut, Bell,
  X, Image, Film, BookOpen, Pen, Globe, Sparkles, Pencil, Smile, Loader2, Plus, Camera, Radio, Wallet, Music, Tv, Heart, Brain,
  User, UserPlus, List, Users, Rocket, Zap, Settings, Star, Search
} from 'lucide-react';
import { checkVideoResolution } from '../../utils/mediaValidation';
import ApologyModal from '../ApologyModal';
import ArtistInviteModal from '../ArtistInviteModal';
import PremiumWelcomeModal from '../PremiumWelcomeModal';
import { DynamicLogo } from './DynamicLogo';

// Emoji picker is lazy-loaded (its dataset is large) so it stays out of the main bundle.
const EmojiPickerLite = React.lazy(() => import('./EmojiPickerLite'));

const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { subscribe, subscribeChat } = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // New Global Create Post Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('post'); // 'post', 'story', or 'reel'
  const [postContent, setPostContent] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const postTextareaRef = useRef(null);

  // Close the emoji picker whenever the composer modal closes (any path).
  useEffect(() => { if (!showCreateModal) setShowEmojiPicker(false); }, [showCreateModal]);

  // Insert an emoji at the caret position in the composer textarea.
  const insertEmoji = (emoji) => {
    const native = emoji?.native || '';
    if (!native) return;
    const el = postTextareaRef.current;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      setPostContent((prev) => prev.slice(0, start) + native + prev.slice(end));
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + native.length;
        try { el.setSelectionRange(pos, pos); } catch (_) {}
      });
    } else {
      setPostContent((prev) => prev + native);
    }
  };

  // Reels specific state
  const [reelVideoFile, setReelVideoFile] = useState(null);
  const [reelCaption, setReelCaption] = useState('');
  const [selectedReelTrack, setSelectedReelTrack] = useState(null);
  const [showReelMusicSelector, setShowReelMusicSelector] = useState(false);
  const [reelMusicSearchQuery, setReelMusicSearchQuery] = useState('');
  const [reelAvailableTracks, setReelAvailableTracks] = useState([]);
  const [reelMusicPlayingTrackId, setReelMusicPlayingTrackId] = useState(null);
  const reelAudioPreviewRef = useRef(new Audio());
  const [reelAudioTrackOffset, setReelAudioTrackOffset] = useState(0.0);
  const [reelAudioTrackVolume, setReelAudioTrackVolume] = useState(1.0);
  const [reelOriginalVolume, setReelOriginalVolume] = useState(0.8);

  const handleReelOffsetChange = (val) => {
    setReelAudioTrackOffset(val);
    if (reelMusicPlayingTrackId) {
      reelAudioPreviewRef.current.currentTime = val;
    }
  };

  const handleReelMusicVolumeChange = (val) => {
    setReelAudioTrackVolume(val);
    reelAudioPreviewRef.current.volume = val;
  };

  const loadReelMusicTracks = async () => {
    try {
      const res = await musicAPI.getTracks();
      setReelAvailableTracks(res.data);
    } catch (err) {
      console.error("Error loading music tracks:", err);
    }
  };

  const handleSearchReelMusic = async (q) => {
    try {
      const res = await musicAPI.searchTracks(q);
      setReelAvailableTracks(res.data);
    } catch (err) {
      console.error("Error searching music tracks:", err);
    }
  };

  const togglePreviewReelTrack = (track) => {
    if (reelMusicPlayingTrackId === track.id) {
      reelAudioPreviewRef.current.pause();
      setReelMusicPlayingTrackId(null);
    } else {
      reelAudioPreviewRef.current.src = track.url.startsWith('/api')
        ? `${process.env.REACT_APP_BACKEND_URL || ''}${track.url}`
        : track.url;
      reelAudioPreviewRef.current.volume = reelAudioTrackVolume;
      reelAudioPreviewRef.current.currentTime = reelAudioTrackOffset;
      reelAudioPreviewRef.current.play().catch(e => console.warn("Preview playback failed:", e));
      setReelMusicPlayingTrackId(track.id);
    }
  };

  const stopPreviewReelTrack = () => {
    reelAudioPreviewRef.current.pause();
    setReelMusicPlayingTrackId(null);
  };

  const handleSelectReelTrack = (track) => {
    setSelectedReelTrack(track);
    setShowReelMusicSelector(false);
    stopPreviewReelTrack();
  };

  useEffect(() => {
    return () => {
      reelAudioPreviewRef.current.pause();
    };
  }, []);
  
  const [notifications, setNotifications] = useState([]);
  const moreRef = useRef(null);
  const notifRef = useRef(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const storyFileInputRef = useRef(null);
  const reelFileInputRef = useRef(null);

  useEffect(() => {
    loadNotifs();
    loadUnreadMessages();

    const handleNotificationsRead = () => {
      setUnreadCount(0);
    };
    const handleNotificationsUpdated = (e) => {
      if (e.detail && typeof e.detail.unreadCount === 'number') {
        setUnreadCount(e.detail.unreadCount);
      } else {
        loadNotifs();
      }
    };

    window.addEventListener('notifications-read', handleNotificationsRead);
    window.addEventListener('notifications-updated', handleNotificationsUpdated);

    return () => {
      window.removeEventListener('notifications-read', handleNotificationsRead);
      window.removeEventListener('notifications-updated', handleNotificationsUpdated);
    };
  }, []);

  // Subscribe to real-time notifications + show toast + play sound
  useEffect(() => {
    const unsub = subscribe((notif) => {
      setNotifications(prev => [notif, ...prev]);
      setUnreadCount(prev => prev + 1);
      toast(notif.fromUserName || t('notifications.title'), {
        description: notif.text,
        duration: 4000,
      });
      try {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
        }
      } catch {}
    });
    return unsub;
  }, [subscribe, t]);

  // Real-time unread badge. Chat events arrive over the WebSocket of the main
  // backend, the same one the Messages page listens to. This used to hang off the
  // Socket.io connection of the separate messenger, which never carried the chat
  // the rest of the app (and the iOS client) actually uses.
  useEffect(() => {
    if (!user) return;
    return subscribeChat((event) => {
      if (event?.type !== 'new_message') return;
      if (event.data?.senderId === user.id) return;
      if (!location.pathname.startsWith('/messages')) {
        setUnreadMessages((prev) => prev + 1);
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
        }
      }
    });
  }, [location.pathname, user, subscribeChat]);

  // Reset unread messages when navigating to messages page, or reload when leaving it
  useEffect(() => {
    if (location.pathname.startsWith('/messages')) {
      setUnreadMessages(0);
    } else {
      loadUnreadMessages();
    }
  }, [location.pathname]);

  const loadUnreadMessages = async () => {
    try {
      const r = await messagesAPI.getUnreadCount();
      setUnreadMessages(r.data.count);
    } catch {}
  };

  useEffect(() => {
    if (!showMore) return;
    const h = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setShowMore(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMore]);

  useEffect(() => {
    if (!showNotifs) return;
    const h = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showNotifs]);

  const loadNotifs = async () => {
    try {
      const r = await notificationsAPI.getNotifications();
      setNotifications(r.data);
      setUnreadCount(r.data.filter(n => !n.read).length);
    } catch {}
  };

  const handleNotifClick = async () => {
    setShowNotifs(!showNotifs);
    setShowMore(false);
    if (!showNotifs && unreadCount > 0) {
      try { await notificationsAPI.markAllRead(); setUnreadCount(0); setNotifications(p => p.map(n => ({...n, read: true}))); } catch {}
    }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const timeAgo = (d) => {
    if (!d) return '';
    const ms = Date.now() - new Date(d).getTime();
    if (isNaN(ms)) return '';
    const s = Math.floor(ms / 1000);
    if (s < 60) return t('common.justNow') || 'now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(ms / 86400000)}d`;
  };

  const navItems = [
    { path: '/feed', icon: House, label: t('nav.home'), key: 'home' },
    { path: '/search', icon: Compass, label: t('nav.explore'), key: 'explore' },
    { path: '/videos', icon: Tv, label: t('nav.videos') || 'Videos', key: 'videos' },
    { path: '/reels', icon: Clapperboard, label: t('nav.reels') || 'Reels', key: 'reels' },
    { path: '/messages', icon: MessageSquare, label: t('nav.messages') || 'Messages', key: 'messages', badge: unreadMessages },
    { path: '/wallet', icon: Wallet, label: t('nav.wallet') || 'Wallet', key: 'wallet' },
    { path: '/music', icon: Music, label: t('nav.music') || 'Music', key: 'music' },
    { path: '/ads', icon: Rocket, label: t('nav.ads') || 'Реклама', key: 'ads' }
  ];

  const isActive = (p) => location.pathname === p || location.pathname.startsWith(p + '/');
  const isReelsPage = location.pathname.startsWith('/reels');
  const isStudioPage = location.pathname.startsWith('/studio');
  const isMessagesPage = location.pathname.startsWith('/messages');
  const isMusicPage = location.pathname.startsWith('/music');
  const isProfilePage = location.pathname.startsWith('/profile');
  const isAdsPage = location.pathname.startsWith('/ads');
  const isBroadcastPage = location.pathname.startsWith('/broadcast');

  // Modal Image Select & Upload
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = '';
    setUploading(true);
    try {
      for (const file of files.slice(0, 4)) {
        let isReelVideo = false;
        if (file.type.startsWith('video/')) {
          const res = await checkVideoResolution(file);
          if (res.is4K && !user?.isPremium) {
            toast.error("Загрузка 4K / High Fidelity видео доступна только Premium-пользователям!");
            continue;
          }
          if (res.height > res.width) {
            isReelVideo = true;
          }
        }
        const r = await uploadAPI.uploadFile(file);
        setSelectedImages(prev => [...prev, { 
          url: `${process.env.REACT_APP_BACKEND_URL || ''}${r.data.url}`, 
          type: r.data.type || 'image',
          isReel: isReelVideo
        }]);
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || t('create.uploadError') || 'Ошибка при загрузке медиа';
      toast.error(errMsg);
    } finally {
      setUploading(false);
    }
  };

  // Publish Post Handler
  const handleCreatePost = async () => {
    if (!postContent.trim() && !selectedImages.length) return;
    setPosting(true);
    try {
      const mediaUrls = selectedImages.length > 0 ? selectedImages.map(m => m.url) : null;
      const hasReelVideo = selectedImages.some(m => m.type === 'video' && m.isReel);
      await postsAPI.createPost(
        postContent,
        mediaUrls,
        false,
        hasReelVideo,
        null,
        false,
        null,
        null,
        null,
        0.0,
        1.0,
        1.0,
        false
      );
      setPostContent('');
      setSelectedImages([]);
      setShowEmojiPicker(false);
      setShowCreateModal(false);
      toast.success(t('create.success') || 'Публикация успешно создана! 🎉');
      
      if (location.pathname === '/feed') {
        window.location.reload();
      } else {
        navigate('/feed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('create.error') || 'Не удалось опубликовать пост');
    } finally {
      setPosting(false);
    }
  };

  // Publish Story Handler
  const handleCreateStory = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setPosting(true);
    try {
      const r = await uploadAPI.uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL || ''}${r.data.url}`;
      await storiesAPI.createStory(imageUrl);
      setShowCreateModal(false);
      toast.success('История успешно создана! 🔮');
      
      if (location.pathname === '/feed') {
        window.location.reload();
      } else {
        navigate('/feed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось загрузить историю');
    } finally {
      setPosting(false);
    }
  };

  // Publish Reel Handler
  const handleCreateReel = async () => {
    if (!reelVideoFile) return;
    setPosting(true);
    try {
      const r = await uploadAPI.uploadFile(reelVideoFile);
      const videoUrl = `${process.env.REACT_APP_BACKEND_URL || ''}${r.data.url}`;
      await postsAPI.createPost(
        reelCaption || '',
        [videoUrl],
        false,
        true,
        null,
        false,
        null,
        null,
        selectedReelTrack?.id,
        reelAudioTrackOffset,
        reelAudioTrackVolume,
        reelOriginalVolume,
        false
      );
      setReelCaption('');
      setReelVideoFile(null);
      setSelectedReelTrack(null);
      setReelAudioTrackOffset(0.0);
      setReelAudioTrackVolume(1.0);
      setReelOriginalVolume(0.8);
      setShowReelMusicSelector(false);
      stopPreviewReelTrack();
      setShowCreateModal(false);
      toast.success('Рилс успешно опубликован! 🎬');
      
      if (location.pathname === '/reels') {
        window.location.reload();
      } else {
        navigate('/reels');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось опубликовать рилс');
    } finally {
      setPosting(false);
    }
  };

  // Character progress circle calculations
  const maxChars = 2500;
  const progressPercent = Math.min(100, (postContent.length / maxChars) * 100);
  const radius = 10;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  return (
    <div className={`min-h-screen transition-colors flex justify-center relative overflow-x-clip ${isReelsPage ? 'dark bg-black text-white' : 'bg-white dark:bg-black'}`}>
      <GlobalCallOverlay />
      <ApologyModal />
      <ArtistInviteModal />
      <PremiumWelcomeModal />
      <Toaster position="top-right" richColors closeButton theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'} />
      <audio ref={audioRef} preload="auto"
        src="data:audio/wav;base64,UklGRl4FAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YToFAACAgICAgICAgICAgICAgICAgICAgICAgH+Bf3+AgYCAgICBgIGAgICBf3+Af4B/f4CAf4B/gH+AgH+Af3+Af4CBgYKCgoODhISFhoaHh4iIiImJiYmJiIiIh4eGhoWEhIOCgYGAf359fHt6eXh3d3Z2dXV1dXV1dXZ2d3h5ent8fX5/gIGCg4SFhoeIiYmKioqKioqJiYiIh4aGhYSEg4KBgH9+fXx7enl4eHd3dnZ2dnZ2d3d4eHl6e3x9fn+AgYKDhIWGh4iIiYqKioqKiomJiIiHhoaFhISDgoGAf35+fXx7enp5eHd3d3d3d3d3eHh5enp7fH1+f4CAgYKDhIWGh4eIiImJiYmJiYiIiIeHhoaFhISDgoKBgH9/fn18fHt6enl5eXl5eXl5enp7e3x9fn5/gICBgoODhIWFhoaHh4iIiIiIiIiIh4eHhoaFhYSEg4OCgYGAf39+fn19fHx8e3t7e3t7e3x8fHx9fX5+f3+AgICBgYKCg4OEhIWFhYaGhoeHh4eHh4eGhoaFhYWEhIODgoKBgYCAf39/fn5+fX19fX19fX19fX1+fn5+f39/gICAgYGBgoKCg4OEhIWFhYWFhYWFhYWFhYWFhYWFhYSEhIODg4KCgoGBgYCAf39/f35+fn5+fn5+fn5+fn5+f39/f4CAgICBgYGCgoKCg4ODg4SEhISEhISEhISEhISDg4ODg4OCgoKCgYGBgICAgH9/f39/f39/f39/f39/f39/f3+AgICAgICBgYGBgYGCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoGBgYGBgICAgICAgH9/f39/f39/f39/f3+AgICAgICAgICAgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==" />

      {!isReelsPage && !isStudioPage && !isMusicPage && !isMessagesPage && !isProfilePage && (
        <header 
          style={{ 
            height: 'calc(60px + env(safe-area-inset-top, 0px))', 
            paddingTop: 'env(safe-area-inset-top, 0px)' 
          }}
          className="mobile-safe-header lg:hidden fixed top-0 left-0 right-0 bg-white dark:bg-black border-b border-gray-200 dark:border-zinc-900 z-50 flex items-center justify-between px-4"
        >
          {/* Left: Avatar trigger */}
          <div onClick={() => setMobileSidebarOpen(true)} className="cursor-pointer shrink-0">
            {user?.avatar && (user.avatar.startsWith('ai-avatar:') || user.avatar.startsWith('linear-gradient')) ? (
              <div style={{ background: user.avatar.startsWith('ai-avatar:') ? user.avatar.substring(10) : user.avatar }} className="w-10 h-10 rounded-full flex items-center justify-center text-[12px] font-bold text-white font-unbounded shadow-sm uppercase">
                {user.displayName?.substring(0, 2) || 'US'}
              </div>
            ) : (
              <img src={user?.avatar} alt=""
                className="w-10 h-10 rounded-full object-cover ring-1 ring-gray-200 dark:ring-zinc-850"
                onError={(e) => { e.target.src='data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>U</text></svg>'; }} />
            )}
          </div>

          {/* Center: Centered Logo Text */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
            <NavLink to="/feed">
              <DynamicLogo className="text-[20px]" />
            </NavLink>
          </div>

          {/* Right: Music Button */}
          <button
            onClick={() => navigate('/music')}
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-black dark:bg-white text-white dark:text-black rounded-full font-bold text-xs hover:opacity-90 transition-opacity shrink-0 z-10"
          >
            <Music className="w-3.5 h-3.5" />
            <span>{t('nav.music') || 'Music'}</span>
          </button>
        </header>
      )}

      {/* MOBILE DRAWER (Slide-out menu) */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-[1px] z-[60] lg:hidden"
              onClick={() => setMobileSidebarOpen(false)}
            />
            {/* Sliding Panel */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="fixed inset-y-0 left-0 w-[280px] max-w-[80vw] bg-black text-white z-[70] flex flex-col lg:hidden border-r border-zinc-900 shadow-2xl overflow-y-auto no-scrollbar"
            >
              {/* Drawer User Info */}
              <div 
                style={{ paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))' }}
                className="mobile-safe-drawer-header p-4 border-b border-zinc-900 pb-5"
              >
                <div className="flex items-center justify-between mb-4">
                  {/* Avatar */}
                  <NavLink to={`/profile/${user?.id}`} onClick={() => setMobileSidebarOpen(false)}>
                    {user?.avatar && (user.avatar.startsWith('ai-avatar:') || user.avatar.startsWith('linear-gradient')) ? (
                      <div style={{ background: user.avatar.startsWith('ai-avatar:') ? user.avatar.substring(10) : user.avatar }} className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white font-unbounded shadow-md uppercase">
                        {user.displayName?.substring(0, 2) || 'US'}
                      </div>
                    ) : (
                      <img src={user?.avatar} alt=""
                        className="w-10 h-10 rounded-full object-cover ring-1 ring-zinc-800"
                        onError={(e) => { e.target.src='data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>U</text></svg>'; }} />
                    )}
                  </NavLink>
                  {/* Plus button */}
                  <button 
                    onClick={() => {
                      setMobileSidebarOpen(false);
                      setShowCreateModal(true);
                      setCreateType('post');
                    }}
                    className="w-7 h-7 rounded-full border border-zinc-800 flex items-center justify-center hover:bg-zinc-900 transition-colors"
                  >
                    <Plus className="w-4 h-4 text-white" />
                  </button>
                </div>
                
                {/* Name & Username */}
                <NavLink to={`/profile/${user?.id}`} onClick={() => setMobileSidebarOpen(false)} className="block">
                  <h2 className="text-[17px] font-bold text-white hover:underline leading-tight">{user?.displayName}</h2>
                  <p className="text-[14px] text-zinc-500 mt-0.5">@{user?.username || user?.displayName?.toLowerCase().replace(/\s+/g, '')}</p>
                </NavLink>
                
                {/* Followers & Following */}
                <div className="flex gap-4 mt-4 text-[13px]">
                  <NavLink to={`/profile/${user?.id}`} onClick={() => setMobileSidebarOpen(false)} className="hover:underline">
                    <span className="font-bold text-white">{user?.followingCount || 0}</span>
                    <span className="text-zinc-500 ml-1">Following</span>
                  </NavLink>
                  <NavLink to={`/profile/${user?.id}`} onClick={() => setMobileSidebarOpen(false)} className="hover:underline">
                    <span className="font-bold text-white">{user?.followersCount || 0}</span>
                    <span className="text-zinc-500 ml-1">Follower{user?.followersCount === 1 ? '' : 's'}</span>
                  </NavLink>
                </div>
              </div>

              {/* Drawer Menu Items */}
              <nav 
                style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}
                className="mobile-safe-drawer-nav flex-1 py-3"
              >
                {[
                  { label: t('nav.profile') || 'Profile', icon: User, path: `/profile/${user?.id}` },
                  { label: t('profile.follow') || 'Follow', icon: UserPlus, path: `/search` },
                  { label: t('nav.music') || 'Music', icon: Music, path: `/music` },
                  { label: t('settings.premiumTitle') || 'Premium', icon: Star, path: `/settings`, state: { section: 'premium' } },
                  { label: t('profile.liked') || 'Liked', icon: Heart, path: `/settings`, state: { section: 'liked' } },
                  { label: t('nav.saved') || 'Bookmarks', icon: Bookmark, path: `/bookmarks` },
                  { label: t('nav.studio') || 'Studio', icon: Radio, path: `/studio` },
                  { label: t('nav.wallet') || 'Wallet', icon: Wallet, path: `/wallet` },
                  { label: t('nav.videos') || 'Videos', icon: Tv, path: `/videos` },
                  { label: t('nav.ads') || 'Реклама', icon: Rocket, path: `/ads` },
                  { label: t('nav.settings') || 'Settings', icon: Settings, path: `/settings` },
                ].map((item, idx) => {
                  return (
                    <NavLink
                      key={idx}
                      to={item.path}
                      state={item.state}
                      onClick={() => setMobileSidebarOpen(false)}
                      className="flex items-center px-6 py-3.5 hover:bg-zinc-950 transition-colors active:bg-zinc-950"
                    >
                      <item.icon className="w-[20px] h-[20px] mr-5 text-white" strokeWidth={2.2} />
                      <span className="text-[17px] font-bold text-white">{item.label}</span>
                    </NavLink>
                  );
                })}
                
                <div className="border-t border-zinc-900 my-2 mx-6" />
                
                <button
                  onClick={() => {
                    setMobileSidebarOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center w-full px-6 py-3.5 hover:bg-zinc-950 transition-colors text-left"
                >
                  <LogOut className="w-[20px] h-[20px] mr-5 text-white" strokeWidth={2.2} />
                  <span className="text-[17px] font-bold text-white">{t('nav.logout') || 'Log out'}</span>
                </button>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* FLOATING ACTION BUTTON (FAB) FOR MOBILE */}
      {!isReelsPage && !isStudioPage && !isMusicPage && !isBroadcastPage && !isMessagesPage && (
        <button
          onClick={() => {
            setShowCreateModal(true);
            setCreateType('post');
          }}
          style={isMobile ? { bottom: 'calc(68px + env(safe-area-inset-bottom, 0px))' } : {}}
          className="mobile-safe-fab lg:hidden fixed bottom-[68px] right-4 z-40 bg-[#1d9bf0] text-white p-3.5 rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-[#1a8cd8] active:scale-95 transition-all"
          data-testid="mobile-fab-post"
        >
          <Plus className="w-6 h-6 stroke-[2.5]" />
        </button>
      )}

      {/* BOTTOM NAV (Mobile + Tablet) — always visible for navigation ease */}
      <nav 
        style={isMobile ? {
          height: 'calc(52px + env(safe-area-inset-bottom, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        } : {}}
        className={`mobile-safe-bottom-nav ${isStudioPage ? 'hidden' : 'lg:hidden'} fixed bottom-0 left-0 right-0 border-t z-50 flex items-center justify-around transition-colors duration-300 h-[52px]
        ${isReelsPage
          ? 'bg-black border-zinc-900 text-white'
          : 'bg-white dark:bg-black border-gray-200 dark:border-zinc-900'
        }`}
      >
        {[
          { path: '/feed', icon: House, key: 'home' },
          { path: '/search', icon: Search, key: 'explore' },
          { path: '/reels', icon: Clapperboard, key: 'reels' },
          { path: '/notifications', icon: Bell, key: 'notifications', badge: unreadCount },
          { path: '/messages', icon: MessageSquare, key: 'messages', badge: unreadMessages },
        ].map((item, idx) => {
          const active = item.isAction ? showNotifs : isActive(item.path);
          if (item.isAction) {
            return (
              <button key={idx} onClick={item.action} data-testid={`mobile-nav-${item.key}`}
                className={`relative flex items-center justify-center w-11 h-11 rounded-xl transition-all ${
                  active
                    ? isReelsPage ? 'text-white' : 'text-gray-950 dark:text-white'
                    : isReelsPage ? 'text-zinc-500 hover:text-zinc-300' : 'text-gray-400'
                }`}>
                <item.icon className="w-[26px] h-[26px]" fill={active ? "currentColor" : "none"} strokeWidth={2} />
                {item.badge > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold px-1">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </button>
            );
          }
          return (
            <NavLink key={item.path} to={item.path} data-testid={`mobile-nav-${item.key}`}
              className={`relative flex items-center justify-center w-11 h-11 rounded-xl transition-all ${
                active
                  ? isReelsPage ? 'text-white' : 'text-gray-950 dark:text-white'
                  : isReelsPage ? 'text-zinc-500 hover:text-zinc-300' : 'text-gray-400'
              }`}>
              {item.isCustomIcon ? (
                <item.icon />
              ) : (
                <>
                  <item.icon className="w-[26px] h-[26px]" fill={active ? "currentColor" : "none"} strokeWidth={2} />
                  {item.badge > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold px-1">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* PREMIUM GLOBAL CREATE MODAL */}
      <AnimatePresence>
      {showCreateModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-md"
            onClick={() => { if (!posting && !uploading) setShowCreateModal(false); }}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 15 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            className="glass-panel bg-white/85 dark:bg-[#161616]/80 w-full max-w-lg rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-800/80 shadow-2xl relative z-10 flex flex-col max-h-[90vh]"
          >
            
            {/* Header Tabs */}
            <div className="flex border-b border-gray-100 dark:border-gray-800/60 relative">
              <button onClick={() => setCreateType('post')}
                className={`flex-1 py-4 text-center font-bold text-[15px] transition-colors ${createType === 'post' ? 'text-gray-950 dark:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                {t('create.post') || 'Публикация'}
              </button>
              <button onClick={() => setCreateType('story')}
                className={`flex-1 py-4 text-center font-bold text-[15px] transition-colors ${createType === 'story' ? 'text-gray-950 dark:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                {t('create.story') || 'История'}
              </button>
              <button onClick={() => setCreateType('reel')}
                className={`flex-1 py-4 text-center font-bold text-[15px] transition-colors ${createType === 'reel' ? 'text-gray-950 dark:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                {t('create.reel') || 'Рилс'}
              </button>
              <button onClick={() => setShowCreateModal(false)} disabled={posting || uploading}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors disabled:opacity-50">
                <X className="w-5 h-5 text-gray-400" />
              </button>
              <motion.div layoutId="modalTabLine" className="absolute bottom-0 h-[3px] bg-blue-500 rounded-full w-1/3" style={{ left: createType === 'post' ? '0%' : createType === 'story' ? '33.33%' : '66.66%' }} />
            </div>

            {/* Content Body */}
            <div className="p-5 flex-1 overflow-y-auto no-scrollbar">
              
              {createType === 'post' ? (
                // --- POST CREATION FORM ---
                <div className="flex gap-4">
                  <img src={user?.avatar} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" onError={(e) => { e.target.src='data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>U</text></svg>'; }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-gray-950 dark:text-white text-sm">@{user?.username || user?.displayName}</span>
                      <span className="flex items-center gap-1 text-[11px] font-semibold bg-gray-100 dark:bg-gray-850 px-2 py-0.5 rounded-full text-gray-500">
                        <Globe className="w-3 h-3" /> Public
                      </span>
                    </div>

                    <textarea
                      ref={postTextareaRef}
                      value={postContent}
                      onChange={(e) => setPostContent(e.target.value)}
                      placeholder={t('feed.whatsHappening') || "Что у вас нового?"}
                      rows={4}
                      className="w-full resize-none border-none outline-none bg-transparent text-[16px] text-gray-900 dark:text-gray-100 placeholder-gray-400 leading-relaxed mt-1"
                    />

                    {/* Selected Media Grid */}
                    {selectedImages.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mt-4">
                        {selectedImages.map((media, idx) => (
                          <div key={media.url} className="relative aspect-video rounded-2xl overflow-hidden bg-gray-900 group">
                            {media.type === 'video' ? (
                              <video src={`${media.url}#t=0.1`} className="w-full h-full object-cover" muted playsInline />
                            ) : (
                              <img src={media.url} alt="" className="w-full h-full object-cover" />
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                            <button
                              onClick={() => setSelectedImages(prev => prev.filter(u => u.url !== media.url))}
                              className="absolute top-2 right-2 w-7 h-7 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : createType === 'story' ? (
                // --- STORY CREATION FORM ---
                <div className="flex flex-col items-center py-6">
                  <div onClick={() => !posting && !uploading && storyFileInputRef.current?.click()}
                    className="w-56 aspect-[9/16] rounded-[28px] border-2 border-dashed border-gray-200 dark:border-gray-800/80 hover:border-blue-500 dark:hover:border-blue-500 cursor-pointer flex flex-col items-center justify-center gap-3 bg-gray-50 dark:bg-black/20 overflow-hidden relative group transition-colors">
                    
                    {posting ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                        <span className="text-xs font-semibold text-gray-400">Публикация...</span>
                      </div>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform">
                           <Camera className="w-6 h-6" />
                        </div>
                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 text-center px-4">
                          Нажмите для выбора фото/видео
                        </span>
                      </>
                    )}
                  </div>
                  <input ref={storyFileInputRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleCreateStory} />
                </div>
              ) : (
                // --- REEL CREATION FORM ---
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4">
                    <img src={user?.avatar} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" onError={(e) => { e.target.src='data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>U</text></svg>'; }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-bold text-gray-950 dark:text-white text-sm">@{user?.username || user?.displayName}</span>
                        <span className="flex items-center gap-1 text-[11px] font-semibold bg-gray-100 dark:bg-gray-850 px-2 py-0.5 rounded-full text-gray-500">
                          <Film className="w-3 h-3 text-blue-500 animate-pulse" /> Reel
                        </span>
                      </div>
                      <textarea
                        value={reelCaption}
                        onChange={(e) => setReelCaption(e.target.value)}
                        placeholder="Добавьте описание для рилса..."
                        rows={3}
                        className="w-full resize-none border-none outline-none bg-transparent text-[16px] text-gray-900 dark:text-gray-100 placeholder-gray-400 leading-relaxed mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex justify-center py-4">
                    {reelVideoFile ? (
                      <div className="relative w-48 aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-lg">
                        <video src={URL.createObjectURL(reelVideoFile)} className="w-full h-full object-cover" controls muted />
                        <button
                          onClick={() => setReelVideoFile(null)}
                          className="absolute top-2 right-2 w-7 h-7 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div onClick={() => !posting && !uploading && reelFileInputRef.current?.click()}
                        className="w-48 aspect-[9/16] rounded-2xl border-2 border-dashed border-zinc-200 dark:border-zinc-800/80 bg-zinc-900/[0.02] dark:bg-white/[0.01] hover:border-sky-500/45 dark:hover:border-sky-500/40 hover:bg-sky-500/[0.02] dark:hover:bg-sky-500/[0.02] cursor-pointer flex flex-col items-center justify-center gap-4 group/upload transition-all duration-300 select-none relative overflow-hidden shadow-inner">
                        
                        {/* Background soft glow on hover */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/0 via-indigo-500/0 to-purple-500/0 group-hover/upload:from-sky-500/[0.02] group-hover/upload:to-indigo-500/[0.02] transition-colors duration-500 pointer-events-none" />
                        
                        <div className="w-12 h-12 rounded-2xl bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 flex items-center justify-center text-zinc-400 dark:text-zinc-500 group-hover/upload:text-sky-400 group-hover/upload:border-sky-500/30 group-hover/upload:scale-105 transition-all duration-300 shadow-sm">
                          <Film className="w-5 h-5 stroke-[1.8]" />
                        </div>
                        
                        <div className="text-center px-3">
                          <span className="text-[11px] font-black block text-zinc-700 dark:text-zinc-300 group-hover/upload:text-zinc-950 dark:group-hover/upload:text-white transition-colors leading-tight">
                            {t('reels.selectVideo') || 'Выбрать видео'}
                          </span>
                          <span className="text-[9px] text-zinc-400 dark:text-zinc-500 mt-1 block leading-tight">
                            {t('create.fromGallery') || 'из галереи'}
                          </span>
                        </div>
                      </div>
                    )}
                    <input ref={reelFileInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => setReelVideoFile(e.target.files?.[0] || null)} />
                  </div>

                  {/* Music Selection Section */}
                  <div className="mb-2 relative">
                    <label className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider block mb-1">{t('create.musicTrack') || 'Музыкальный трек'}</label>
                    {selectedReelTrack ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-gray-900 dark:text-white">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-sky-400 to-indigo-600 flex items-center justify-center flex-shrink-0">
                              <Music className="w-4 h-4 text-white" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-bold truncate">{selectedReelTrack.title}</p>
                              <p className="text-[10px] text-zinc-500 truncate">{selectedReelTrack.artist}</p>
                            </div>
                          </div>
                          <button 
                            type="button" 
                            onClick={() => setSelectedReelTrack(null)}
                            className="text-xs text-red-500 hover:text-red-400 font-extrabold"
                          >
                            {t('create.remove') || 'Удалить'}
                          </button>
                        </div>
                        
                        {/* Trimmer & Volume Sliders */}
                        <div className="p-3.5 rounded-2xl bg-zinc-50/50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/80 space-y-3.5">
                          {/* Audio Trimmer / Start Offset */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider">{t('create.audioTrimmer') || 'Начало воспроизведения (Обрезка)'}</span>
                              <span className="text-xs text-sky-500 dark:text-sky-400 font-black">{reelAudioTrackOffset}s</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="30"
                              step="0.5"
                              value={reelAudioTrackOffset}
                              onChange={(e) => handleReelOffsetChange(parseFloat(e.target.value))}
                              className="w-full h-1 bg-zinc-200 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-sky-505"
                            />
                            <div className="flex justify-between text-[8px] text-zinc-400 mt-0.5">
                              <span>0s ({t('create.start') || 'Старт'})</span>
                              <span>15s</span>
                              <span>30s ({t('create.max') || 'Макс'})</span>
                            </div>
                          </div>

                          {/* Volume controls */}
                          <div className="grid grid-cols-2 gap-3.5">
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider">{t('create.videoVolume') || 'Громкость видео'}</span>
                                <span className="text-xs text-zinc-650 dark:text-zinc-300 font-bold">{Math.round(reelOriginalVolume * 100)}%</span>
                              </div>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={reelOriginalVolume}
                                onChange={(e) => setReelOriginalVolume(parseFloat(e.target.value))}
                                className="w-full h-1 bg-zinc-200 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-zinc-500"
                              />
                            </div>
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider">{t('create.musicVolume') || 'Громкость музыки'}</span>
                                <span className="text-xs text-sky-500 dark:text-sky-400 font-black">{Math.round(reelAudioTrackVolume * 100)}%</span>
                              </div>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={reelAudioTrackVolume}
                                onChange={(e) => handleReelMusicVolumeChange(parseFloat(e.target.value))}
                                className="w-full h-1 bg-zinc-200 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-sky-505"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowReelMusicSelector(true);
                          loadReelMusicTracks();
                        }}
                        className="w-full py-3 border border-dashed border-zinc-300 dark:border-zinc-800 hover:border-sky-500/40 rounded-2xl flex items-center justify-center gap-2 text-zinc-500 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white transition-all text-xs font-bold bg-zinc-50/[0.02] dark:bg-white/[0.01]"
                      >
                        <Music className="w-4 h-4 animate-pulse" />
                        {t('create.chooseMusic') || 'Выбрать музыку для видео'}
                      </button>
                    )}

                    {/* Inline Music Selector Overlay */}
                    {showReelMusicSelector && (
                      <div className="absolute inset-x-0 bottom-0 bg-white dark:bg-[#0c0c0e] border border-zinc-200 dark:border-zinc-800 rounded-[28px] p-5 z-50 flex flex-col shadow-2xl max-h-[320px]">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xs font-black text-gray-955 dark:text-white">{t('create.chooseMusic') || 'Выберите музыкальный трек'}</h4>
                          <button 
                            type="button" 
                            onClick={() => {
                              setShowReelMusicSelector(false);
                              stopPreviewReelTrack();
                            }}
                            className="p-1 rounded-full bg-zinc-100 dark:bg-white/5 hover:bg-zinc-200 dark:hover:bg-white/10 text-zinc-400"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        
                        <div className="mb-3">
                          <input
                            type="text"
                            placeholder={t('create.searchTracks') || 'Поиск треков, артистов...'}
                            value={reelMusicSearchQuery}
                            onChange={(e) => {
                              setReelMusicSearchQuery(e.target.value);
                              handleSearchReelMusic(e.target.value);
                            }}
                            className="w-full bg-zinc-50 dark:bg-white/[0.02] border border-zinc-200 dark:border-zinc-900 rounded-xl px-4 py-2 text-xs text-gray-900 dark:text-white placeholder-zinc-400 focus:border-sky-500/30 outline-none"
                          />
                        </div>

                        <div className="flex-1 overflow-y-auto no-scrollbar space-y-1.5 max-h-[180px]">
                          {reelAvailableTracks.length === 0 ? (
                            <p className="text-center text-[10px] text-zinc-500 py-6">{t('create.noTracksFound') || 'Треки не найдены'}</p>
                          ) : (
                            reelAvailableTracks.map(track => {
                              const isPlaying = reelMusicPlayingTrackId === track.id;
                              return (
                                <div 
                                  key={track.id} 
                                  className="flex items-center justify-between p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-white/5 transition-colors border border-transparent hover:border-zinc-200 dark:hover:border-white/5 cursor-pointer"
                                  onClick={() => handleSelectReelTrack(track)}
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        togglePreviewReelTrack(track);
                                      }}
                                      className="w-7 h-7 rounded-lg bg-sky-500/10 hover:bg-sky-500/20 flex items-center justify-center text-sky-400 flex-shrink-0"
                                    >
                                      <Music className={`w-3.5 h-3.5 ${isPlaying ? 'animate-bounce' : ''}`} />
                                    </button>
                                    <div className="min-w-0">
                                      <p className="text-xs font-bold text-gray-900 dark:text-white truncate">{track.title}</p>
                                      <p className="text-[10px] text-zinc-500 truncate">{track.artist}</p>
                                    </div>
                                  </div>
                                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-zinc-200 dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 uppercase font-semibold">
                                    {track.genre}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions (Only for Post type) */}
            {createType === 'post' && (
              <div className="p-4 border-t border-gray-100 dark:border-gray-800/60 bg-gray-50/50 dark:bg-black/10 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading || posting}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors disabled:opacity-50">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Image className="w-5 h-5" />}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleImageSelect} />
                  
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker((v) => !v)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${showEmojiPicker ? 'text-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60'}`}
                    >
                      <Smile className="w-5 h-5" />
                    </button>
                    {showEmojiPicker && (
                      <>
                        {/* Outside-click backdrop */}
                        <div className="fixed inset-0 z-[59]" onClick={() => setShowEmojiPicker(false)} />
                        <div className="absolute bottom-full left-0 mb-2 z-[60] shadow-2xl rounded-xl overflow-hidden">
                          <React.Suspense fallback={
                            <div className="w-[352px] h-[435px] rounded-xl bg-white dark:bg-[#1d1e23] border border-gray-200 dark:border-white/10 flex items-center justify-center">
                              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                            </div>
                          }>
                            <EmojiPickerLite
                              locale={(i18n?.language || 'en').startsWith('ru') ? 'ru' : 'en'}
                              theme={typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                              onSelect={(emoji) => { insertEmoji(emoji); }}
                            />
                          </React.Suspense>
                        </div>
                      </>
                    )}
                  </div>

                </div>

                <div className="flex items-center gap-3">
                  {/* Twitter character count circle */}
                  {postContent.length > 0 && (
                    <div className="relative w-6 h-6 flex items-center justify-center">
                      <svg className="w-6 h-6 transform -rotate-90">
                        <circle cx="12" cy="12" r={radius} className="text-gray-200 dark:text-gray-800" strokeWidth="2.5" fill="transparent" />
                        <circle cx="12" cy="12" r={radius} className={postContent.length >= maxChars ? 'text-red-500' : 'text-blue-500'} strokeWidth="2.5" fill="transparent"
                          strokeDasharray={circumference}
                          strokeDashoffset={strokeDashoffset}
                        />
                      </svg>
                      {postContent.length >= maxChars - 20 && (
                        <span className="absolute text-[9px] font-bold text-red-500">{maxChars - postContent.length}</span>
                      )}
                    </div>
                  )}

                  <button
                    onClick={handleCreatePost}
                    disabled={posting || uploading || (!postContent.trim() && !selectedImages.length) || postContent.length > maxChars}
                    className="px-6 py-2.5 bg-gradient-to-r from-[#38bdf8] to-blue-500 text-white font-bold text-[14px] rounded-full shadow-md shadow-blue-500/10 hover:shadow-lg hover:shadow-blue-500/20 transition-all disabled:opacity-50 active:scale-95 flex items-center gap-2"
                  >
                    {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : (t('feed.post') || 'Опубликовать')}
                  </button>
                </div>
              </div>
            )}

            {createType === 'reel' && (
              <div className="p-4 border-t border-gray-100 dark:border-gray-800/60 bg-gray-50/50 dark:bg-black/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                </div>
                <button
                  onClick={handleCreateReel}
                  disabled={posting || !reelVideoFile}
                  className="px-6 py-2.5 bg-gradient-to-r from-[#38bdf8] to-blue-500 text-white font-bold text-[14px] rounded-full shadow-md shadow-blue-500/10 hover:shadow-lg hover:shadow-blue-500/20 transition-all disabled:opacity-50 active:scale-95 flex items-center gap-2"
                >
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : (t('reels.publish') || 'Опубликовать рилс')}
                </button>
              </div>
            )}

          </motion.div>
        </div>
      )}
      </AnimatePresence>

      <div className={`w-full ${isStudioPage || isMusicPage || isAdsPage ? 'max-w-none' : 'max-w-[1280px]'} flex relative`}>
      {/* SIDEBAR (Desktop) — always visible */}
      <aside className={`${isStudioPage || isMusicPage || isAdsPage ? 'hidden' : 'hidden lg:flex'} flex-col sticky top-0 h-screen w-[240px] xl:w-[268px] flex-shrink-0 border-r z-50 transition-colors duration-300
        ${isReelsPage 
          ? 'bg-black border-zinc-900 text-white' 
          : 'bg-white dark:bg-black border-gray-200 dark:border-zinc-900'
        }`}>
        <div className="px-6 pt-8 pb-6">
          <NavLink to="/feed" className="flex items-center gap-2" data-testid="sidebar-logo">
            <DynamicLogo className="text-[26px]" />
          </NavLink>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {navItems.map(item => {
            const active = isActive(item.path);
            return (
              <NavLink key={item.path} to={item.path} data-testid={`nav-${item.key}`}
                className={`flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] transition-all duration-200 group active:scale-[0.97]
                  ${active
                    ? isReelsPage
                      ? 'text-white font-bold bg-white/5'
                      : 'text-gray-950 dark:text-white font-bold'
                    : isReelsPage
                      ? 'text-zinc-400 hover:bg-white/5 hover:text-white font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white font-medium'
                  }`}>
                <div className="relative">
                  <item.icon className="w-[24px] h-[24px] transition-transform duration-300 group-hover:scale-110 group-active:scale-90" fill={active ? "currentColor" : "none"} strokeWidth={active ? 2 : 2} />
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold px-1 ring-2 ring-white dark:ring-gray-950" data-testid="unread-messages-badge">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                {item.label}
              </NavLink>
            );
          })}

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button onClick={handleNotifClick} data-testid="nav-notifications"
              className={`flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] w-full transition-all duration-200 group font-medium
                ${showNotifs
                  ? isReelsPage
                    ? 'bg-zinc-805 text-white font-semibold bg-white/5 shadow-sm'
                    : 'bg-gray-950 dark:bg-white text-white dark:text-gray-950 font-semibold shadow-sm'
                  : isReelsPage
                    ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                }`}>
              <div className="relative">
                <BellRing className="w-[24px] h-[24px] transition-transform duration-200 group-hover:scale-105" fill={showNotifs ? "currentColor" : "none"} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold px-1 ring-2 ring-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              {t('nav.notifications')}
            </button>
            <AnimatePresence>
            {showNotifs && (
              <motion.div
                initial={{ opacity: 0, x: -10, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -10, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className={`absolute left-full top-0 ml-3 w-[340px] rounded-2xl shadow-2xl border z-50 overflow-hidden
                  ${isReelsPage
                    ? 'bg-zinc-900 border-zinc-800 text-white'
                    : 'bg-white dark:bg-[#1a1a1a] border-gray-100 dark:border-gray-800'
                  }`}>
                <div className={`px-5 py-4 border-b flex items-center justify-between ${isReelsPage ? 'border-zinc-800' : 'border-gray-100 dark:border-gray-800'}`}>
                  <h3 className={`font-heading font-bold text-base ${isReelsPage ? 'text-white' : 'text-gray-950 dark:text-white'}`}>{t('notifications.title')}</h3>
                  {unreadCount > 0 && <span className="text-xs text-blue-600 font-medium">{t('notifications.new', { count: unreadCount })}</span>}
                </div>
                <div className={`max-h-[400px] overflow-y-auto divide-y ${isReelsPage ? 'divide-zinc-800' : 'divide-gray-50 dark:divide-gray-800'}`}>
                  {notifications.length === 0 ? (
                    <div className="py-12 text-center">
                      <Bell className="w-10 h-10 text-gray-200 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm">{t('notifications.empty')}</p>
                    </div>
                  ) : notifications.map((n, i) => (
                    <motion.div key={n.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => navigate(n.link || '/notifications')} className={`flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/80 dark:hover:bg-gray-800/50 cursor-pointer transition-colors ${!n.read ? (isReelsPage ? 'bg-zinc-850' : 'bg-blue-50/30 dark:bg-blue-950/20') : ''}`}>
                      {n.fromUserAvatar && (n.fromUserAvatar.startsWith('ai-avatar:') || n.fromUserAvatar.startsWith('linear-gradient')) ? (
                        <div style={{ background: n.fromUserAvatar.startsWith('ai-avatar:') ? n.fromUserAvatar.substring(10) : n.fromUserAvatar }} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-[12px] font-bold text-white font-unbounded select-none shadow-sm ring-1 ring-gray-100 dark:ring-gray-700 uppercase">
                          {n.fromUserName?.substring(0, 2) || 'U'}
                        </div>
                      ) : (
                        <img src={n.fromUserAvatar || 'data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>' + (n.fromUserName?.substring(0, 1) || 'U').toUpperCase() + '</text></svg>'} alt=""
                          className="w-10 h-10 rounded-full object-cover ring-1 ring-gray-100 dark:ring-gray-700 flex-shrink-0"
                          onError={(e) => { e.target.onerror=null; e.target.src='data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>' + (n.fromUserName?.substring(0, 1) || 'U').toUpperCase() + '</text></svg>'; }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-snug ${isReelsPage ? 'text-zinc-300' : 'text-gray-800 dark:text-gray-200'}`}><span className={`font-semibold ${isReelsPage ? 'text-white' : 'text-gray-950 dark:text-white'}`}>{n.fromUserName}</span> {n.text}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{timeAgo(n.createdAt)}</p>
                      </div>
                      {!n.read && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0" />}
                    </motion.div>
                  ))}
                </div>
                <div className={`px-5 py-3 border-t ${isReelsPage ? 'border-zinc-800' : 'border-gray-100 dark:border-gray-800'}`}>
                  <button onClick={() => { setShowNotifs(false); navigate('/notifications'); }}
                    className="w-full text-center text-xs font-semibold text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    data-testid="see-all-notifications">
                    {t('notifications.seeAll') || 'See all notifications'}
                  </button>
                </div>
              </motion.div>
            )}
            </AnimatePresence>
          </div>

          {/* Profile */}
          <NavLink to={`/profile/${user?.id}`} data-testid="nav-profile"
            className={`flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] transition-all duration-200 group
              ${location.pathname === `/profile/${user?.id}`
                ? isReelsPage
                  ? 'text-white font-bold bg-white/5'
                  : 'text-gray-950 dark:text-white font-bold'
                : isReelsPage
                  ? 'text-zinc-400 hover:bg-white/5 hover:text-white font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white font-medium'
              }`}>
            {user?.avatar && (user.avatar.startsWith('ai-avatar:') || user.avatar.startsWith('linear-gradient')) ? (
              <div style={{ background: user.avatar.startsWith('ai-avatar:') ? user.avatar.substring(10) : user.avatar }} className={`w-[24px] h-[24px] rounded-full flex items-center justify-center text-[8px] font-bold text-white font-unbounded transition-all duration-200 group-hover:scale-105 shadow-sm ${location.pathname === `/profile/${user?.id}` ? 'ring-2 ring-gray-900 dark:ring-white' : 'ring-1 ring-gray-200 dark:ring-gray-600'}`}>
                {user.displayName?.substring(0, 2) || 'US'}
              </div>
            ) : (
              <img src={user?.avatar} alt=""
                className={`w-[24px] h-[24px] rounded-full object-cover transition-all duration-200 group-hover:scale-105
                  ${location.pathname === `/profile/${user?.id}` ? 'ring-2 ring-gray-900 dark:ring-white' : 'ring-1 ring-gray-200 dark:ring-gray-600'}
                `}
                onError={(e) => { e.target.src='data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><rect width=\'100\' height=\'100\' fill=\'%236366f1\'/><text x=\'50\' y=\'50\' fill=\'white\' font-family=\'sans-serif\' font-size=\'40\' text-anchor=\'middle\' alignment-baseline=\'middle\'>U</text></svg>'; }} />
            )}
            {t('nav.profile')}
          </NavLink>

          <div className="pt-4 px-2">
            <button onClick={() => { setShowCreateModal(true); setCreateType('post'); }} data-testid="sidebar-post-btn"
              className="w-full py-3 bg-[#1d9bf0] hover:bg-[#1a8cd8] text-white rounded-full font-bold text-[15px] transition-all duration-200 active:scale-[0.98]">
              {t('nav.create') || 'Post'}
            </button>
          </div>
        </nav>

        {/* More menu */}
        <div className="relative px-3 pb-6 pt-2" ref={moreRef}>
          <button onClick={() => { setShowMore(!showMore); setShowNotifs(false); }} data-testid="nav-more"
            className={`flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] font-medium w-full transition-all duration-200 group
              ${isReelsPage
                ? 'text-zinc-400 hover:bg-white/5 hover:text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
              }`}>
            <MoreHorizontal className="w-[24px] h-[24px] transition-transform duration-200 group-hover:scale-105" /> {t('nav.more')}
          </button>
          {showMore && (
            <div className={`absolute bottom-full left-3 right-3 mb-2 rounded-2xl shadow-2xl border z-50 py-2 overflow-hidden
              ${isReelsPage
                ? 'bg-zinc-900 border-zinc-800 text-white'
                : 'bg-white dark:bg-[#1a1a1a] border-gray-100 dark:border-gray-800'
              }`}>
              <NavLink to="/settings" state={{ section: 'premium' }} onClick={() => setShowMore(false)} className="flex items-center gap-3 px-5 py-3 text-[15px] font-bold text-amber-500 hover:bg-amber-500/5 transition-colors">
                <Sparkles className="w-5 h-5 text-amber-500 fill-amber-500/10" /> VeonFox Premium
              </NavLink>
              <NavLink to="/settings" onClick={() => setShowMore(false)}
                className={`flex items-center gap-3 px-5 py-3 text-[15px] font-medium transition-colors
                  ${isReelsPage 
                    ? 'text-zinc-300 hover:bg-white/5' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}><SlidersHorizontal className="w-5 h-5" /> {t('nav.settings')}</NavLink>
              <NavLink to="/settings" state={{ section: 'liked' }} onClick={() => setShowMore(false)}
                className={`flex items-center gap-3 px-5 py-3 text-[15px] font-medium transition-colors
                  ${isReelsPage 
                    ? 'text-zinc-300 hover:bg-white/5' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}><Heart className="w-5 h-5" /> {t('profile.liked') || 'Понравилось'}</NavLink>
              <NavLink to="/bookmarks" onClick={() => setShowMore(false)}
                className={`flex items-center gap-3 px-5 py-3 text-[15px] font-medium transition-colors
                  ${isReelsPage 
                    ? 'text-zinc-300 hover:bg-white/5' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}><Bookmark className="w-5 h-5" /> {t('nav.saved')}</NavLink>
              <NavLink to="/studio" onClick={() => setShowMore(false)}
                className={`flex items-center gap-3 px-5 py-3 text-[15px] font-medium transition-colors
                  ${isReelsPage 
                    ? 'text-zinc-300 hover:bg-white/5' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}><Radio className="w-5 h-5" /> {t('nav.studio') || 'Studio'}</NavLink>
              {user?.isAdmin && <NavLink to="/vox-hq" onClick={() => setShowMore(false)}
                className={`flex items-center gap-3 px-5 py-3 text-[15px] font-medium transition-colors
                  ${isReelsPage 
                    ? 'text-zinc-300 hover:bg-white/5' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}><ShieldCheck className="w-5 h-5" /> {t('nav.admin')}</NavLink>}
              <div className={`border-t mx-4 my-1 ${isReelsPage ? 'border-zinc-800' : 'border-gray-100 dark:border-gray-800'}`} />
              <button onClick={handleLogout}
                className={`flex items-center gap-3 px-5 py-3 text-[15px] font-medium w-full transition-colors
                  ${isReelsPage 
                    ? 'text-zinc-300 hover:bg-white/5' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}><LogOut className="w-5 h-5" /> {t('nav.logout')}</button>
            </div>
          )}
        </div>
      </aside>



      {isReelsPage || isStudioPage || isMusicPage || isMessagesPage || isAdsPage || isBroadcastPage ? (
        <main className="flex-1 w-full min-w-0">{children}</main>
      ) : (
        <div 
          style={isMobile ? { 
            paddingTop: isProfilePage 
              ? 'env(safe-area-inset-top, 0px)' 
              : 'calc(60px + env(safe-area-inset-top, 0px))', 
            paddingBottom: 'calc(52px + env(safe-area-inset-bottom, 0px))' 
          } : {}}
          className={`mobile-safe-content ${isProfilePage ? 'profile-mobile-content' : ''} flex-1 w-full min-w-0 lg:pt-0 lg:pb-0 lg:!pt-0 lg:!pb-0`}
        >
          <main>
            <div className={`max-w-[1024px] mx-auto ${isProfilePage ? 'px-0 sm:px-6' : 'px-4 sm:px-6'} py-6 lg:py-0`}>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {children}
              </motion.div>
            </div>
          </main>
        </div>
      )}
      </div>
    </div>
  );
};

export default MainLayout;
